/* eslint-disable no-continue */
import React, {
	type ReactNode,
	useEffect,
	useMemo,
	useRef,
	useState,
	useCallback,
	Fragment,
	type ComponentPropsWithoutRef,
} from 'react';
import { graphql, useFragment } from 'react-relay';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { useSkipLink, useSkipLinkId } from '@atlaskit/navigation-system/layout/skip-links';
import { createRouterSelector, useRouterActions } from '@atlassian/react-resource-router';
import { TabListWithAnalytics } from '@atlassian/jira-horizontal-nav-common/src/ui/TabListWithAnalytics.tsx';
import type {
	ContainerNavigationTabs_container$key,
	ContainerNavigationTabs_container$data,
	JiraNavigationItemTypeKey,
} from '@atlassian/jira-relay/src/__generated__/ContainerNavigationTabs_container.graphql';
import AddTab from '@atlassian/jira-horizontal-nav-add-tab/src/ui/AddTab.tsx';
import { TabShortcutsContextProvider } from '@atlassian/jira-horizontal-nav-common/src/common/tab-shortcut-context/index.tsx';
import type { TabsDescriptor } from '@atlassian/jira-horizontal-nav-common/src/common/types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIsProjectPagesOptedIn } from '@atlassian/jira-navigation-apps-common/src/controllers/is-project-pages-opted-in/index.tsx';
import { useDroppableTabList } from '../controllers/droppable-tab-list/index.tsx';
import { NavigationItemTab } from './NavigationItemTab.tsx';
import { NINListMergeObserver } from './NINListMergeObserver.tsx';

export const useLocation = createRouterSelector((state) => state.location);

type NavigationItemNode = NonNullable<
	NonNullable<
		NonNullable<ContainerNavigationTabs_container$data['navigationItems']>['edges']
	>[number]
>['node'];

export type Props = {
	containerFragment: ContainerNavigationTabs_container$key;
	tabsDescriptor: TabsDescriptor;
	label: string;
	renderWithTab?: (navigationItemNode: NavigationItemNode) => ReactNode;
	tabToHighlight?: string;
	onDisableHighlight?: (tabKey: string) => void;
};

type ItemWithUrl = { url: string; id: string };

/**
 * The logic in this hook determines the selected tab in the horizontal navigation and
 * redirects the user to the not found page if the selected tab is not found.
 *
 * There are a few caveats to this logic:
 * - We do not want to redirect to the not found page if the user is on the browse page.
 * - We do not want to redirect to the not found page if the user just removed the tab
 *   they were on and redirection to the default tab is yet to happen.
 *
 * For this reason, we only recalculate the selected tab id when the route or the navigation container has changed.
 */
const useSelectedTabId = (containerData: ContainerNavigationTabs_container$data) => {
	const prevUrl = useRef<string | null>(null);
	const { replace } = useRouterActions();
	const location = useLocation();
	const currentUrl = decodeURIComponent(location.pathname) + location.search;
	const selectedTabIdRef = useRef<string | null>(null);
	const notFoundUrl = `${containerData.scopeUrl}/not-found`;

	// if the current URL or the container has changed, reset the selected tab id
	if (prevUrl.current !== currentUrl) {
		selectedTabIdRef.current = null;
		prevUrl.current = currentUrl;
	}

	// if the selected tab id hasn't been set yet, try to find it
	if (
		selectedTabIdRef.current == null &&
		containerData?.navigationItems?.edges &&
		location.pathname !== notFoundUrl
	) {
		let fullMatchResult: string | null = null;
		let partialMatchResult: string | null = null;

		const sortedItems = containerData.navigationItems.edges
			.flatMap((edge): ItemWithUrl | ItemWithUrl[] => {
				const url = edge?.node.url;
				if (!url) {
					return [];
				}

				const id = edge.node.id;
				const item = { url, id };

				// consider URLs of nested apps
				if (edge.node.typeKey === 'APP') {
					const sections = edge.node.sections;
					if (sections) {
						const appItems: ItemWithUrl[] = [];
						sections.forEach((section) => {
							section?.links?.forEach((link) => {
								if (link?.url) {
									appItems.push({ url: link.url, id });
								}
							});
						});

						return [item, ...appItems];
					}
				}

				return item;
			})
			.sort((a, b) => {
				if (!a.url || !b.url) {
					return 0;
				}
				return a.url.length - b.url.length;
			});

		const legacySelectedItem = new URLSearchParams(location.search).get('selectedItem');

		// check for matches of the tab url with the current url
		for (const item of sortedItems) {
			// in case of legacy pages, we only check for the selectedItem query param
			if (legacySelectedItem) {
				const itemQueryParams = new URLSearchParams(item.url.split('?')[1]);
				if (itemQueryParams.get('selectedItem') === legacySelectedItem) {
					fullMatchResult = item.id;
					break;
				}
				continue;
			}

			// if the url is a full match, return straight away
			if (item.url === currentUrl) {
				fullMatchResult = item.id;
				break;
			}

			// if the start of the path matches, store the id and continue checking
			if (item.url && currentUrl.startsWith(item.url)) {
				partialMatchResult = item.id;
			}
		}

		selectedTabIdRef.current = fullMatchResult ?? partialMatchResult;
	}

	const selectedTabId = selectedTabIdRef.current;
	const shouldRedirectToNotFound =
		!selectedTabId &&
		location.pathname !== notFoundUrl &&
		!location.pathname.startsWith('/browse/');

	useEffect(() => {
		if (shouldRedirectToNotFound) {
			replace(notFoundUrl);
		}
	}, [shouldRedirectToNotFound, notFoundUrl, replace]);

	return selectedTabId;
};

export const ContainerNavigationTabs = ({
	containerFragment,
	tabsDescriptor,
	label,
	renderWithTab,
	tabToHighlight,
	onDisableHighlight,
}: Props): React.JSX.Element => {
	const [itemWithRename, setItemWithRename] = useState<string | null>(null);
	const tabListRef = useRef<HTMLDivElement>(null);
	const containerData = useFragment(
		graphql`
			fragment ContainerNavigationTabs_container on JiraContainerNavigationResult {
				...NavigationItemTab_container
				...AddTab_container
				... on JiraContainerNavigation {
					id @required(action: THROW)
					canEdit @required(action: THROW)
					scopeUrl
					# Add @required(action: THROW) to scopeId when cleaning up
					scopeId
					navigationItems(first: $first) {
						edges {
							node @required(action: THROW) {
								__id
								id @required(action: THROW)
								url
								typeKey
								# Including label here even though it's not used directly in this component to force
								# tabsAndIds to update when a tab label changes, e.g. when a tab is renamed.
								# This causes tab overflow to be recalculated which may move tabs in/out of the more menu.
								# eslint-disable-next-line @atlassian/relay/unused-fields
								label
								... on JiraAppNavigationItem {
									appId
									sections {
										links {
											url
										}
									}
								}
								...NavigationItemTab_tab
							}
						}
					}
				}
			}
		`,
		containerFragment,
	);

	const selectedTabId = useSelectedTabId(containerData);

	const onAddItem = useCallback(
		({ id, typeKey }: { id: string; typeKey: JiraNavigationItemTypeKey }) => {
			if (typeKey === 'APP' || !containerData?.navigationItems?.edges) {
				// ignore apps
				return;
			}

			// check whether there is already a tab with the same typeKey
			const hasItemOfType = containerData.navigationItems.edges.some(
				(edge) => edge?.node.typeKey === typeKey,
			);

			// if an item with the same typeKey already exists, trigger the rename form for it
			if (hasItemOfType) {
				setItemWithRename(id);
			}
		},
		[containerData?.navigationItems?.edges],
	);

	const { isOptedIn } = useIsProjectPagesOptedIn();

	const { tabsAndIds, droppableTabs } = useMemo(() => {
		const tabsAll = Array.from(containerData?.navigationItems?.edges ?? []).filter(Boolean);

		const tabs = isOptedIn ? tabsAll : tabsAll.filter((edge) => !edge?.node?.id.endsWith('/pages'));

		const tabElements = tabs?.map((edge, index) => (
			<Fragment key={edge.node.id}>
				<NavigationItemTabStable
					position={index}
					tabFragment={edge.node}
					containerFragment={containerData}
					beforeItemId={index === 0 ? null : tabs[index - 1]?.node?.id}
					afterItemId={index === tabs.length - 1 ? null : tabs[index + 1]?.node?.id}
					tabsDescriptor={tabsDescriptor}
					isRenaming={edge.node.id === itemWithRename}
					setItemWithRename={setItemWithRename}
					id={edge.node.id}
					shouldHighlight={tabToHighlight === edge.node.typeKey}
					onDisableHighlight={onDisableHighlight}
				/>
				{renderWithTab?.(edge.node)}
			</Fragment>
		));
		return {
			tabsAndIds:
				tabs?.map((edge, index) => ({ id: edge?.node?.id, tab: tabElements[index] })) ?? [],
			droppableTabs: tabs.map((tab) => ({
				id: tab.node.id,
				typeKey: tab.node.typeKey,
				appId: tab.node.appId,
				dataId: tab.node.__id,
			})),
		};
	}, [
		containerData,
		isOptedIn,
		tabsDescriptor,
		itemWithRename,
		tabToHighlight,
		onDisableHighlight,
		renderWithTab,
	]);

	useDroppableTabList(droppableTabs, containerData.id ?? '', containerData.scopeId ?? '');

	const projectNavigationId = useSkipLinkId();

	useSkipLink(projectNavigationId, label, 3);

	return (
		<TabShortcutsContextProvider navigationName={label}>
			{/* Delete NINListMergeObserver when cleaning up jira_list_nin_merge */}
			{/* eslint-disable-next-line jira/ff/no-preconditioning  */}
			{fg('jira_list_nin_merge_technical_changes') && !fg('jira_list_nin_merge') && (
				<NINListMergeObserver tabs={droppableTabs} />
			)}
			<TabListWithAnalytics
				id={projectNavigationId}
				ref={tabListRef}
				label={label}
				addTab={
					containerData.canEdit && (
						<SpotlightTarget name="container-navigation-tabs-add-button">
							<AddTab
								containerFragment={containerData}
								tabsDescriptor={tabsDescriptor}
								onAddItem={onAddItem}
							/>
						</SpotlightTarget>
					)
				}
				selectedTabId={selectedTabId}
				tabs={tabsAndIds}
			/>
		</TabShortcutsContextProvider>
	);
};

const NavigationItemTabStable = ({
	position,
	tabFragment,
	containerFragment,
	beforeItemId,
	afterItemId,
	tabsDescriptor,
	isRenaming,
	id,
	setItemWithRename,
	shouldHighlight,
	onDisableHighlight,
}: Omit<ComponentPropsWithoutRef<typeof NavigationItemTab>, 'onRenamingChange'> & {
	id: string;
	setItemWithRename: (isRenaming: string | null) => void;
}) => {
	const onRenamingChange = useCallback(
		(isRename: boolean) => setItemWithRename(isRename ? id : null),
		[id, setItemWithRename],
	);
	return (
		<NavigationItemTab
			position={position}
			tabFragment={tabFragment}
			containerFragment={containerFragment}
			beforeItemId={beforeItemId}
			afterItemId={afterItemId}
			tabsDescriptor={tabsDescriptor}
			isRenaming={isRenaming}
			onRenamingChange={onRenamingChange}
			shouldHighlight={shouldHighlight}
			onDisableHighlight={onDisableHighlight}
		/>
	);
};
